/**
 * @file src/utils/datetime.js
 * @description THE single place that turns a backend timestamp into something
 * a human reads. Import from here rather than hand-rolling a `new Date(...)`.
 *
 * The API emits UTC ISO-8601 with an explicit offset:
 *
 *     "2026-07-29T13:29:45+00:00"
 *
 * Rendering that value directly into JSX — which a surprising number of views
 * did — puts the raw technical string on screen ("2026-07-29T13:29:45+00:00")
 * AND leaves it in UTC, so a venue at UTC+2 sees 13:29 for a file it uploaded
 * at 15:29. It only ever looked acceptable because the RETIRED wire format was
 * a naive 'Y-m-d H:i' that the server had already pre-shifted — human-ish by
 * accident, and wrong in a different way.
 *
 * These helpers parse the offset and render in the VIEWER's own timezone.
 *
 * The legacy naive format is still accepted (interpreted as UTC, which is how
 * it was always stored) so a stale cached response degrades gracefully instead
 * of printing garbage.
 */

/**
 * Parse a backend timestamp into a Date, or null if unparseable.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseApiDate(value) {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

    const raw = String(value).trim();
    if (!raw) return null;

    // NUMERIC EPOCH. Not every endpoint serializes through Eloquent: the loyalty
    // order feed emits `Carbon::valueOf()` — a millisecond epoch NUMBER — on both
    // the REST payload and the RTDB mirror. Those values have no 'T', so they
    // fell through to the string branch below as `new Date("1785771583000Z")`,
    // which is an Invalid Date. The caller then saw null and rendered nothing:
    // the kitchen lost its ticket age, its overdue warning and its oldest-first
    // sort in one go, silently.
    //
    // Magnitudes are checked rather than guessed. >= 1e12 is milliseconds;
    // 1e9..1e12 is seconds (both ranges start in 2001, comfortably before this
    // product existed). Anything smaller is not a plausible timestamp — a bare
    // "20260803" must keep returning null instead of resolving to 1970.
    if (/^\d+$/.test(raw)) {
        const n = Number(raw);
        if (!Number.isFinite(n)) return null;
        if (n < 1e9) return null;
        const d = new Date(n >= 1e12 ? n : n * 1000);
        return isNaN(d.getTime()) ? null : d;
    }

    // ISO (has a 'T') is self-describing — it carries its own offset.
    // A zone-less legacy value was always UTC, so pin it with a 'Z'.
    const d = raw.includes('T')
        ? new Date(raw)
        : new Date(`${raw.replace(' ', 'T')}Z`);

    return isNaN(d.getTime()) ? null : d;
}

/**
 * Date + time in the viewer's timezone, e.g. "29 Jul 2026, 15:29".
 * @param {string|Date} value
 * @param {string} [fallback='—'] shown when the value is missing/unparseable
 */
export function formatDateTime(value, fallback = '—') {
    const d = parseApiDate(value);
    return d
        ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        : fallback;
}

/**
 * Date only, in the viewer's timezone, e.g. "29 Jul 2026".
 * @param {string|Date} value
 * @param {string} [fallback='—']
 */
export function formatDate(value, fallback = '—') {
    const d = parseApiDate(value);
    return d ? d.toLocaleDateString(undefined, { dateStyle: 'medium' }) : fallback;
}

/**
 * Time only, in the viewer's timezone, e.g. "15:29".
 * @param {string|Date} value
 * @param {string} [fallback='—']
 */
export function formatTime(value, fallback = '—') {
    const d = parseApiDate(value);
    return d ? d.toLocaleTimeString(undefined, { timeStyle: 'short' }) : fallback;
}

const RELATIVE_DIVISIONS = [
    { amount: 60, unit: 'second' },
    { amount: 60, unit: 'minute' },
    { amount: 24, unit: 'hour' },
    { amount: 7, unit: 'day' },
    { amount: 4.34524, unit: 'week' },
    { amount: 12, unit: 'month' },
    { amount: Infinity, unit: 'year' },
];

/**
 * Relative time in Modern Standard Arabic, e.g. "قبل ساعتين". Built on
 * Intl.RelativeTimeFormat (no new dependency — moment's default locale
 * isn't Arabic anywhere in this app) so it gets real MSA plural/dual forms
 * for free, unlike a hand-rolled "N hours ago" string.
 * @param {string|Date} value
 * @param {string} [fallback='—']
 */
export function formatRelativeArabic(value, fallback = '—') {
    const d = parseApiDate(value);
    if (!d) return fallback;

    let duration = (d.getTime() - Date.now()) / 1000;
    // numberingSystem forced to 'latn' — Arabic's default CLDR numbering
    // system is Arabic-Indic digits (٣، ٢، ١), but this product's Arabic UI
    // convention is Arabic TEXT with LATIN digits everywhere (see
    // Board/Dashboard.jsx's nfInt/nf1). Without this, "last seen" read e.g.
    // "منذ ٥ دقائق" instead of the intended "منذ 5 دقائق".
    const rtf = new Intl.RelativeTimeFormat('ar', { numeric: 'auto', numberingSystem: 'latn' });

    for (const division of RELATIVE_DIVISIONS) {
        if (Math.abs(duration) < division.amount) {
            return rtf.format(Math.round(duration), division.unit);
        }
        duration /= division.amount;
    }
    return fallback;
}
